import { createContext, useContext, type ReactNode } from 'react';
import type { ApprovedUnit } from '@bms2/shared';
import { PAGES_PATH, useApi } from '../pages/records/api';

/**
 * The approved units every line-item grid in the frame reads (decision 0439):
 * the `Unit` rows piece 6 seeds with `isSaleUnit`, fetched once by the shell
 * rather than by each document page.
 *
 * 🔨 The Item Table takes no `apiBaseUrl` and asks the network for nothing; it
 * reads this list the way a control reads the viewer's zone, so a document page
 * inherits the closed unit list without being edited. 💰 The default is an
 * empty list rather than a guess, so a grid rendered outside the frame draws
 * the unit locked instead of offering units nobody seeded.
 */
export const APPROVED_UNITS_PATH = `${PAGES_PATH}/approved-units`;

const ApprovedUnitsContext = createContext<readonly ApprovedUnit[]>([]);

export function ApprovedUnitsProvider({ units, children }: { units: readonly ApprovedUnit[]; children: ReactNode }) {
  return <ApprovedUnitsContext.Provider value={units}>{children}</ApprovedUnitsContext.Provider>;
}

export function useApprovedUnits(): readonly ApprovedUnit[] {
  return useContext(ApprovedUnitsContext);
}

/** The shell's own read, kept beside the provider so nothing else has to know the path. */
export function useApprovedUnitsRead(apiBaseUrl: string): readonly ApprovedUnit[] {
  const read = useApi<{ rows: ApprovedUnit[] }>(APPROVED_UNITS_PATH, apiBaseUrl, { cache: true });
  return read.state.kind === 'ready' ? read.state.data.rows : [];
}
